import { Clipboard } from '@angular/cdk/clipboard';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Injectable,
  Injector,
  input,
  signal
} from '@angular/core';
import { Reservation } from '@appTypes/api.types';
import { displayTraceId } from '@core/interceptors/trace-context';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { reservationRefresh, reservationsSearch } from '@pages/reservations-page/state/reservations.action';
import { PriceFormatPipe } from '@shared/pipes/price-format.pipe';
import { finalize } from 'rxjs';
import {
  formatPlanDate,
  formatPlanMoney,
  formatPlanPercent,
  PAYMENT_STEP_STATUS_CLASSES,
  PAYMENT_STEP_STATUS_KEYS,
  PaymentListState,
  paymentPlanErrorMessage,
  PaymentStepStatus,
  readPaymentPlan,
  textOrNull
} from './next-payment.model';

/**
 * ADLC-4 agency payment rules on the website (QA doc: docs features/agency-payment-rules/README.md R-W1 .. R-W8; prototype
 * features/agency-payment-rules/mockups/02-website-next-payment-prototype.html). One component, four places:
 *   - `band`     the "Next payment" item of the reservation's general-info band (R-W1 .. R-W3, R-W6);
 *   - `schedule` the Payment schedule tab - a table from 820 px, cards below (R-W5);
 *   - `prices`   the next-payment line and the steps in the Prices dialog (R-W4);
 *   - `list`     the coded box above the result list when payments cannot be read (R-W7 / R-W8, C17).
 * Display only: every amount, percent and date is the API's (D1); the error carries its code and Trace ID (C3 / C23).
 */

export type NextPaymentVariant = 'band' | 'schedule' | 'prices' | 'list';

/** The reservations whose payment plan is being re-read after "Try again", shared by the band and the tab of the same card. */
@Injectable({ providedIn: 'root' })
export class PaymentPlanRetryTracker {
  readonly #ids = signal<ReadonlySet<string>>(new Set());

  isRetrying(id: string): boolean {
    return this.#ids().has(id);
  }

  start(id: string): void {
    this.#ids.update((ids) => new Set(ids).add(id));
  }

  stop(id: string): void {
    this.#ids.update((ids) => {
      const next = new Set(ids);
      next.delete(id);
      return next;
    });
  }
}

interface StepRow {
  stepNo: number;
  due: string;
  percent: string;
  amount: string;
  amountWithCurrency: string;
  paid: string;
  paidWithCurrency: string;
  outstanding: string;
  outstandingWithCurrency: string;
  status: PaymentStepStatus;
  statusKey: string;
  statusParams: { value: number } | undefined;
  statusClass: string;
  isLate: boolean;
  /** "To pay" below zero (an overpaid step) - shown red like every negative money figure (D1). */
  isNegative: boolean;
  isNext: boolean;
}

const COPIED_RESET_MS = 2000;

@Component({
  selector: 'app-next-payment',
  imports: [TranslatePipe, NgTemplateOutlet, PriceFormatPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './next-payment.component.html',
  styleUrl: './next-payment.component.scss'
})
export class NextPaymentComponent {
  /**
   * The NGXS Store is read only when "Try again" is pressed: `Store` is root-provided, so an eager inject in a host without NGXS
   * (the general-info band, the Prices dialog, their specs) fails on NGXS_OPTIONS even when marked optional.
   */
  readonly #injector = inject(Injector);
  readonly #clipboard = inject(Clipboard);
  readonly #tracker = inject(PaymentPlanRetryTracker);
  #copiedTimer: ReturnType<typeof setTimeout> | undefined;

  public readonly variant = input<NextPaymentVariant>('band');
  public readonly reservation = input<Reservation | null | undefined>(null);
  /** Several reservations and payments are down: the card says only "Next payment: not available" (R-W7). */
  public readonly listDown = input(false);
  /** `list` variant: the state of the whole result list. */
  public readonly listState = input<PaymentListState | null>(null);
  /** `list` variant: the search is running again after "Try again". */
  public readonly listRetrying = input(false);

  public readonly copied = signal(false);

  public readonly plan = computed(() => readPaymentPlan(this.reservation()));
  /** The reservation uuid: what "Try again" re-reads (reservationRefresh) and the retry tracker is keyed by. */
  public readonly reservationId = computed(() => this.reservation()?.id ?? '');
  /** The RF number the agency sees: the `<rf>` of the payment test ids (README test ids, KZ-BUG-261). */
  public readonly rfid = computed(() => textOrNull(this.reservation()?.number) ?? '');
  /** The list is down AND this reservation's payments are among those that did not answer: "Next payment: not available" (R-W7). */
  public readonly listDownHere = computed(() => this.listDown() && this.plan().state === 'unavailable');
  public readonly retrying = computed(() => !!this.reservationId() && this.#tracker.isRetrying(this.reservationId()));
  public readonly traceId = computed(() => displayTraceId(this.plan().traceId));
  public readonly listTraceId = computed(() => displayTraceId(this.listState()?.traceId));
  /** The readable message of the box's error code (PAYMENT_PLANS_UNAVAILABLE -> "Payment schedules are not available right now"). */
  public readonly errorMessage = computed(() => paymentPlanErrorMessage(this.plan().errorCode));
  public readonly listErrorMessage = computed(() => paymentPlanErrorMessage(this.listState()?.errorCode));

  public readonly next = computed(() => this.plan().next);
  public readonly isLate = computed(() => this.next()?.kind === 'overdue');

  /** `{a} ({p}) by {d}` / `{a} ({p}) since {d}` parameters, the amount with the invoice currency. */
  public readonly nextParams = computed(() => {
    const next = this.next();
    if (!next) return { a: '', p: '', d: '' };
    return {
      a: formatPlanMoney(next.amount, textOrNull(next.currencyCode) ?? this.plan().currencyCode),
      p: formatPlanPercent(next.percentOfTotal),
      d: formatPlanDate(next.dueDate)
    };
  });

  /** Q-269 (docs#620): the Prices dialog shows the amount in its own column with the currency of the payment, like the dialog's other rows. */
  public readonly nextCurrency = computed(() => textOrNull(this.next()?.currencyCode) ?? this.plan().currencyCode);

  /** No currency known: the plain amount, never converted to the active currency (D1). */
  public planMoney(amount: number | null | undefined): string {
    return formatPlanMoney(amount);
  }

  public readonly steps = computed<StepRow[]>(() => {
    const schedule = this.plan().schedule;
    if (!schedule) return [];
    const currency = schedule.currencyCode;
    return schedule.steps.map((step) => {
      const isLate = step.status === 'overdue';
      const hasDays = isLate && step.overdueDays !== null && step.overdueDays !== undefined;
      return {
        stepNo: step.stepNo,
        due: formatPlanDate(step.dueDate),
        percent: formatPlanPercent(step.percent),
        amount: formatPlanMoney(step.amount),
        amountWithCurrency: formatPlanMoney(step.amount, currency),
        paid: formatPlanMoney(step.paid),
        paidWithCurrency: formatPlanMoney(step.paid, currency),
        outstanding: formatPlanMoney(step.outstanding),
        outstandingWithCurrency: formatPlanMoney(step.outstanding, currency),
        status: step.status,
        // an overdue step the sender sent without its day count says only "Overdued" (the filter's own word)
        statusKey: isLate && !hasDays ? 'Overdued' : (PAYMENT_STEP_STATUS_KEYS[step.status] ?? step.status),
        statusParams: hasDays ? { value: step.overdueDays as number } : undefined,
        statusClass: PAYMENT_STEP_STATUS_CLASSES[step.status] ?? '',
        isLate,
        isNegative: (step.outstanding ?? 0) < 0,
        isNext: !!step.isNext && !isLate
      };
    });
  });

  public readonly summaryParams = computed(() => {
    const schedule = this.plan().schedule;
    return { n: schedule?.steps.length ?? 0, t: formatPlanMoney(schedule?.total, schedule?.currencyCode) };
  });

  public readonly closed = computed(() => !!this.plan().schedule?.closed || this.next()?.kind === 'closed');

  public readonly listText = computed(() =>
    this.listState()?.prefix === 'reservations-overdued'
      ? 'Overdue status is not available right now.'
      : 'Payment information is not available right now. The reservations are shown without the next payment; the Overdued status follows the standard rule.'
  );

  constructor() {
    inject(DestroyRef).onDestroy(() => clearTimeout(this.#copiedTimer));
  }

  copy(id: string | undefined): void {
    if (!id) return;
    this.copied.set(this.#clipboard.copy(id));
    clearTimeout(this.#copiedTimer);
    this.#copiedTimer = setTimeout(() => this.copied.set(false), COPIED_RESET_MS);
  }

  /** "Try again" on one reservation: re-read only that reservation (the rest of the page stays as it is). */
  retry(): void {
    const id = this.reservationId();
    if (!id || this.retrying()) return;
    const store = this.#store();
    if (!store) return;
    this.#tracker.start(id);
    store
      .dispatch(new reservationRefresh(id))
      .pipe(finalize(() => this.#tracker.stop(id)))
      .subscribe({ error: () => undefined });
  }

  /** "Try again" on the list box: run the same search again, on the same page. */
  retryList(): void {
    if (this.listRetrying()) return;
    this.#store()
      ?.dispatch(new reservationsSearch({}))
      .subscribe({ error: () => undefined });
  }

  /** The Store, or null when this host has no NGXS set up (then "Try again" does nothing). */
  #store(): Store | null {
    try {
      return this.#injector.get(Store, null);
    } catch {
      return null;
    }
  }
}
